import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../../components/Card";
import Loader from "../../components/partials/components/loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FiClock, FiRefreshCw } from "react-icons/fi";
import { getApiCall } from "../../service/httpCall";
import { API_DASHBOARD_IVR_LISTING } from "../../service/url";

const MAX_LISTING_DAYS = 180;
const AUTO_REFRESH_SECONDS = 60;

const normalizeRows = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.rows)) return resp.rows;
  return [];
};

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = startOfDay(value);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDateForCell = (value) => {
  const date = safeDate(value);
  if (!date) return { date: "--", time: "" };
  return {
    date: date.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
};

const statusBodyTemplate = () => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "5px 12px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
      background: "#c45800",
      color: "#ffffff",
    }}
  >
    <span style={{ lineHeight: 1 }}>●</span>
    Pending
  </span>
);

const dateTimeBodyTemplate = (parts) => (
  <div>
    <div style={{ fontWeight: 600, color: "#2b2f36" }}>{parts.date}</div>
    {parts.time ? (
      <div style={{ fontSize: "12px", color: "#7a8699", marginTop: "2px" }}>
        {parts.time}
      </div>
    ) : null}
  </div>
);

function DashboardIvrList() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [refreshCountdownSec, setRefreshCountdownSec] =
    useState(AUTO_REFRESH_SECONDS);

  const loadIvrListing = useCallback((showLoader = false) => {
    const today = startOfDay(new Date());
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - (MAX_LISTING_DAYS - 1));
    const query = `?startDate=${encodeURIComponent(
      toDateInputValue(startDate)
    )}&endDate=${encodeURIComponent(toDateInputValue(today))}&status=pending`;

    if (showLoader) {
      setLoading(true);
    }

    return getApiCall(`${API_DASHBOARD_IVR_LISTING}${query}`)
      .then((resp) => {
        setRows(normalizeRows(resp));
      })
      .catch(() => {
        setRows([]);
      })
      .finally(() => {
        if (showLoader) {
          setLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    loadIvrListing(true);

    const refreshInterval = setInterval(() => {
      loadIvrListing(false);
      setRefreshCountdownSec(AUTO_REFRESH_SECONDS);
    }, AUTO_REFRESH_SECONDS * 1000);

    const countdownInterval = setInterval(() => {
      setRefreshCountdownSec((prev) => {
        if (prev <= 1) {
          return AUTO_REFRESH_SECONDS;
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [loadIvrListing]);

  const tableRows = useMemo(
    () =>
      (Array.isArray(rows) ? rows : []).map((r, idx) => ({
        key: r?.id || idx + 1,
        flow_name: r?.flow_name || "--",
        status: Number(r?.status),
        updated_at_parts: formatDateForCell(r?.updated_at),
        created_at_parts: formatDateForCell(r?.created_at),
      })),
    [rows]
  );

  if (loading) return <Loader />;

  return (
    <Card className="shadow-sm border-0" style={{ padding: "10px" }}>
      <Card.Header
        className="d-flex align-items-center justify-content-between"
        style={{ gap: 12, flexWrap: "wrap" }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 15,
            color: "var(--dash-text)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: "10px",
          }}
        >
          <span>Pending IVRs</span>
          {tableRows.length > 0 && (
            <span className="dash-count-badge">{tableRows.length}</span>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            loadIvrListing(false);
            setRefreshCountdownSec(AUTO_REFRESH_SECONDS);
          }}
          style={{
            border: "1px solid var(--dash-border)",
            background: "var(--dash-surface-alt)",
            color: "var(--dash-text-soft)",
            borderRadius: 999,
            height: 32,
            padding: "0 12px",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: "10px",
          }}
          title="Refresh Pending IVRs"
        >
          <FiRefreshCw size={13} />
          {refreshCountdownSec}s
        </button>
      </Card.Header>
      <Card.Body>
        <DataTable
          value={tableRows}
          paginator
          rows={10}
          emptyMessage="No pending IVRs found"
          className="p-datatable-sm"
          tableStyle={{ minWidth: "100%" }}
        >
          <Column field="flow_name" header="IVR Name" />
          <Column field="status" header="Status" body={statusBodyTemplate} />
          <Column
            field="updated_at"
            header="Last Updated"
            body={(rowData) => (
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                <FiClock size={13} style={{ color: "#7a8699" }} />
                <div>{dateTimeBodyTemplate(rowData.updated_at_parts)}</div>
              </div>
            )}
          />
          <Column
            field="created_at"
            header="Created At"
            body={(rowData) => dateTimeBodyTemplate(rowData.created_at_parts)}
          />
        </DataTable>
      </Card.Body>
    </Card>
  );
}

export default React.memo(DashboardIvrList);
